/**
 * Хуки уведомлений: лента, отметки прочтения и матрица настроек каналов.
 *
 * Оптимистики нет: счётчик и список перечитываются после ответа сервера, на
 * время запроса кнопка блокируется, ошибка уходит тостером — как в остальных
 * мутациях v2.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  fetchNotificationPreferencesV2,
  fetchNotificationsV2,
  markAllNotificationsReadV2,
  markNotificationReadV2,
  updateNotificationPreferenceV2,
} from '../api/notifications';
import type { NotificationTypeV2 } from '../api/notifications';
import type { NotificationChannel } from './types';

export const notificationKeys = {
  all: ['notifications-v2'] as const,
  list: (limit: number) => ['notifications-v2', 'list', limit] as const,
  preferences: ['notifications-v2', 'preferences'] as const,
};

/** Лента непрочитанных. Обновляется сама раз в минуту, как колокольчик и ждёт. */
export function useNotificationsV2(limit = 30, enabled = true) {
  return useQuery({
    queryKey: notificationKeys.list(limit),
    queryFn: () => fetchNotificationsV2({ limit }),
    enabled,
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
  });
}

export function useNotificationActionsV2() {
  const qc = useQueryClient();

  const refresh = () =>
    qc.invalidateQueries({ queryKey: notificationKeys.all });

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationReadV2(id),
    onSuccess: refresh,
  });

  const markAllRead = useMutation({
    mutationFn: markAllNotificationsReadV2,
    onSuccess: refresh,
  });

  return {
    markRead,
    markAllRead,
    isBusy: markRead.isPending || markAllRead.isPending,
  };
}

/* ------------------------------------------------------------ настройки --- */

/** Ключ строки матрицы: тип события + канал. */
function prefKey(eventType: string, channel: string) {
  return `${eventType}:${channel}`;
}

export type PreferenceMatrix = {
  isEnabled: (
    eventType: NotificationTypeV2,
    channel: NotificationChannel
  ) => boolean;
  /** true, если строку бэк реально хранит (а не подставлен дефолт). */
  isExplicit: (
    eventType: NotificationTypeV2,
    channel: NotificationChannel
  ) => boolean;
};

/**
 * Матрица «тип события × канал». Бэк отдаёт только явно выставленные строки,
 * поэтому недостающие считаем включёнными: пока пользователь ничего не менял,
 * уведомления по умолчанию приходят.
 */
export function useNotificationPreferencesV2(enabled = true) {
  const query = useQuery({
    queryKey: notificationKeys.preferences,
    queryFn: fetchNotificationPreferencesV2,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const matrix = useMemo<PreferenceMatrix>(() => {
    const stored = new Map(
      (query.data ?? [])
        .filter((pref) => pref.eventType && pref.channel)
        .map((pref) => [
          prefKey(String(pref.eventType), String(pref.channel)),
          Boolean(pref.enabled),
        ])
    );

    return {
      isEnabled: (eventType, channel) =>
        stored.get(prefKey(eventType, channel)) ?? true,
      isExplicit: (eventType, channel) =>
        stored.has(prefKey(eventType, channel)),
    };
  }, [query.data]);

  return { ...query, matrix };
}

export function useUpdateNotificationPreferenceV2() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      eventType: NotificationTypeV2;
      channel: NotificationChannel;
      enabled: boolean;
    }) => updateNotificationPreferenceV2(payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: notificationKeys.preferences }),
  });
}
